import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useCreatePolicyMutation } from "@dashboard/policies/api";
import { Typography } from "@mui/material";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import { policyNameExists } from "../../PoliciesPage.utils";

type FormValues = {
    name: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onCreated: (policyId: string) => void;
    policies: API.PolicyViewModel[];
};

const validate = (values: FormValues, policies: API.PolicyViewModel[]) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name?.trim()) {
        errors.name = i18next.t("modals:createPolicy.input.name.validation.required");
    } else if (policyNameExists(policies, values.name)) {
        errors.name = i18next.t("modals:createPolicy.input.name.validation.exists");
    }
    return errors;
};

const CreatePolicyModal = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const [createPolicy, { isLoading }] = useCreatePolicyMutation();

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        if (workspaceId === null) {
            return;
        }

        try {
            const result = await createPolicy({ workspaceId, name: values.name.trim(), content: [] }).unwrap();
            showSuccessToast(t("modals:createPolicy.toast.createSuccess"));
            form.restart();
            props.onCreated(result.model.id);
            props.onClose();
        } catch (error) {
            showErrorToast(getErrorMessage(error));
        }
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            e.preventDefault();
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={(values) => validate(values, props.policies)}
            initialValues={{ name: "" }}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        onClose={props.onClose}
                        title={t("modals:createPolicy.title")}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton onClick={() => form.submit()} disabled={isLoading} loading={isLoading}>
                                {t("common:button.create")}
                            </PrimaryButton>
                        }
                    >
                        <Typography>{t("modals:createPolicy.hint")}</Typography>
                        <TextField name="name" label={t("common:label.name")} fullWidth required autoFocus />
                    </Modal>
                </form>
            )}
        />
    );
};

export default CreatePolicyModal;
